import {
    CanActivate,
    Router,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
    CanActivateChild,
    CanLoad,
    Route,
    RouterState,
} from '@angular/router';
import { areRoutesLoaded, selectAllRoutes, selectRouteByURL } from './redux/routes/routes.selectors';
import { take, map, switchMap, first, debounceTime, Observable, of, from, timeout, catchError } from 'rxjs';
import { SecurityService } from './shared/services/security.service';
import { enterZone } from './shared/utils/enter-zone';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { ModuleSwitchService } from './shared/services/module-switch.service';
import { isRouteModuleOn, moduleForRoute } from '@newgenus/common';
import { allRoutesLoaded } from './redux/routes/routes.actions';
import { AuthService } from './shared/services/auth.service';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { NewgenusRoute, User } from '@newgenus/common';
import { AppState } from './redux/reducers';
import { select, Store } from '@ngrx/store';
import { Injectable, NgZone } from '@angular/core';
import { metaObject } from './global';
import { userUpdate } from './redux/user/user.actions';
import { routeMatchesUrl } from './redux/routes/route-match';

type Purpose = 'CanActivate' | 'CanActivateChild' | 'CanLoad';

interface UserAuth {
    url: string;
    user: User;
    purpose: Purpose;
    authenticated: boolean | null;
    authorized: boolean | null;
    redirect: string | null;
    meta?: any;
}

/**
 * Protect routes that should only be viewed by:
 * 1 - authenticated and initilized (via user service) and
 * 2 - authorized (via feature service)
 * users.
 */
@Injectable()
export class AuthGuard implements CanActivate, CanActivateChild, CanLoad {
    state: RouterState;
    snapshot: RouterStateSnapshot;
    root: ActivatedRouteSnapshot;
    child: any;
    url: any;

    constructor(
        private authService: AuthService,
        public afAuth: AngularFireAuth,
        public db: AngularFirestore,
        private router: Router,
        private security: SecurityService,
        public store: Store<AppState>,
        private moduleSwitch: ModuleSwitchService,
        private zone: NgZone
    ) {
        this.state = router.routerState;
        this.url = router.url;
        this.snapshot = this.state.snapshot;
        this.root = this.snapshot.root;
        this.child = this.root.firstChild;
    }

    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.check(state.url, 'CanActivate').pipe(
            map((userAuth) => {
                if (!userAuth.authenticated) {
                    // console.log("userAuth 1: ", userAuth);
                    this.router.navigate(['/login']);
                    return false;
                } else if (!userAuth.authorized) {
                    // console.log("userAuth 2: ", userAuth);
                    this.router.navigate([userAuth.redirect]);
                    return false;
                } else {
                    // console.log("userAuth 3: ", userAuth);
                    return true;
                }
            })
        );
    }

    public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.check(state.url, 'CanActivateChild');
    }

    public canLoad(route: Route): Observable<boolean> {
        return this.check(route.path as string, 'CanLoad');
    }

    // private functions ---------------------------------------------------------

    // The answer comes from fire-compat streams (sign-in, module switches) that emit OUTSIDE the
    // Angular zone; handing it back inside keeps the navigation — and every page it builds — in the
    // zone. Without this a cold load (refresh / deep link) built a page whose inputs and buttons
    // never triggered change detection (the issues search, ISS-033).
    private check(url: string, purpose: Purpose): Observable<any> {
        // Take first value or timeout after 3 seconds
        return this.authService.user$.pipe(
            first(),
            // timeout(5000),
            switchMap((user) => {
                return this.checkAuthenticated({
                    authenticated: null,
                    authorized: null,
                    purpose,
                    redirect: null,
                    url,
                    user,
                });
            }),
            map((userAuth) => {
                this.authService.user = userAuth.user;
                return userAuth;
            }),
            switchMap((userAuth) => this.checkAuthorized(userAuth)),
            map((userAuth) => {
                // console.log('auth guard userAuth: ', userAuth);
                if (userAuth.redirect) {
                    if (!userAuth.authenticated) {
                        this.authService.redirectUrl = url;
                    }
                    if (!userAuth.authorized) {
                        this.authService.redirectUrl = url;
                    }
                }
                return userAuth; // userAuth.authenticated && userAuth.authorized;; //  && userAuth.authorized;
            }),
            catchError((error) => {
                // console.error(`Auth request timed out after: 5s > error`, error);
                return of(false);
            }),
            enterZone(this.zone)
        );
    }

    private checkAuthenticated(userAuth: UserAuth): Observable<UserAuth> {
        if (userAuth.user === null) {
            // console.log("step 1!");
            userAuth.authenticated = false;
            userAuth.redirect = userAuth.redirect || '/login';
            return of(userAuth);
        } else {
            // console.log("step 2!", userAuth);
            userAuth.authenticated = true;
            return of(userAuth);
        }
    }

    private checkAuthorized(userAuth: UserAuth): Observable<UserAuth> {
        return from(
            new Promise<UserAuth>((resolve, reject) => {
                if (userAuth.user === null) {
                    // not logged in
                    console.error('No user session.');
                    reject();
                }

                // const featureKeys = userAuth.user.organizations[userAuth.user.organizations.selected].features;

                this.fetchRoutesByURL(userAuth.url)
                    .pipe(first(), debounceTime(300))
                    .toPromise()
                    .then(async (routes) => {
                        let meta = {} as any;

                        // Onboarding routes a user with NO valid org must still reach — the
                        // create-org wizard and the home screen (which hosts the "create your
                        // first org" CTA). Without this, a brand-new signup (zero orgs) can
                        // never get anywhere and onboarding is impossible.
                        const url = userAuth.url || '';
                        const isOnboardingRoute =
                            url.startsWith('/app/organization/new') || url.startsWith('/app/home');
                        const authorizeOnboarding = () => {
                            userAuth.authorized = true;
                            userAuth['meta'] = meta;
                            metaObject['userAuth'] = userAuth;
                            this.security.userAuth = userAuth;
                            resolve(userAuth);
                        };

                        if (!routes || routes.length === 0) {
                            console.error('checkAuthorized > fetchRoutesByURL > routes.length === 0');
                            // reject("routes not loaded into store yet");
                            if (userAuth.user.isDeveloper) {
                                userAuth.authorized = true;
                                userAuth['meta'] = meta;
                                metaObject['userAuth'] = userAuth;
                                this.security.userAuth = userAuth;
                                resolve(userAuth);
                                return;
                            }
                        }

                        if (!userAuth.user.organizations) {
                            // Brand-new user with no organizations map at all — a zero-org
                            // user. Allow onboarding routes (wizard / home); bounce the rest
                            // home. NB: the bare `return` here used to hang the promise (no
                            // resolve/reject), which froze the guard for new users.
                            if (isOnboardingRoute) {
                                authorizeOnboarding();
                            } else {
                                userAuth.authorized = false;
                                userAuth.redirect = '/app/home';
                                resolve(userAuth);
                            }
                            return;
                        }

                        // Defensive: the currently selected org may have been deleted (e.g. wizard
                        // org cleaned up server-side) — `selected` then either points at a key with
                        // no map entry, or is undefined. Fall back to the first valid org the user
                        // still has membership in. If none, send the user to the home screen rather
                        // than letting `.features` blow up below.
                        const orgsMap = userAuth.user.organizations as any;
                        const selectedKey = orgsMap.selected;
                        const isSelectedValid = selectedKey && orgsMap[selectedKey];
                        if (!isSelectedValid) {
                            const candidateIds: string[] = Array.isArray(orgsMap.orgIds) ? orgsMap.orgIds : [];
                            const fallback = candidateIds.find((id) => id && orgsMap[id]);
                            if (fallback) {
                                console.warn(
                                    `AuthGuard > selected org <${selectedKey}> is no longer valid; falling back to <${fallback}>.`
                                );
                                orgsMap.selected = fallback;
                                // Persist the fallback so subsequent guards/effects pick it up and the
                                // user doc stays consistent with the actual membership map.
                                this.store.dispatch(
                                    userUpdate({ payload: { organizations: orgsMap } as Partial<User> })
                                );
                            } else if (isOnboardingRoute) {
                                // Zero valid orgs but heading to the wizard / home — let them
                                // through so they can create their first org.
                                authorizeOnboarding();
                                return;
                            } else {
                                console.warn(
                                    `AuthGuard > user <${userAuth.user?.email}> has no valid orgs; redirecting to /app/home.`
                                );
                                userAuth.authorized = false;
                                userAuth.redirect = '/app/home';
                                resolve(userAuth);
                                return;
                            }
                        }

                        const featureKeys = userAuth.user.organizations[userAuth.user.organizations.selected].features;

                        // Dark-deploy switchboard (DARK-DEPLOY-MODULE-SWITCHES-DESIGN.md §3b): a route whose
                        // module is dark is closed to everyone — the developer bypass below is INSIDE an enabled
                        // module only. Wait for the first switch snapshot so a dev/stage user is not judged by
                        // production's fallback surface during the first paint.
                        await this.moduleSwitch.ready();
                        const moduleCtx = {
                            orgId: userAuth.user.organizations.selected,
                            userFeatures: featureKeys,
                            isDeveloper: !!userAuth.user.isDeveloper,
                        };

                        let routeFeatureExists = false;
                        // setup features meta
                        for await (const route of routes || []) {
                            // routes.forEach(async (route) => {
                            meta['features'] = route.features;
                            meta = { ...meta, ...route };

                            // const routeFound = routes.find(r => r.route === userAuth.url);
                            const featureKeyFound = featureKeys.find((item) => item === route.routeFeature);

                            // console.log('checkAuthorized > featureKeyFound:', featureKeyFound);
                            // console.log('checkAuthorized > onRoute doc:', route);
                            // console.log('checkAuthorized > featureKeys:', featureKeys);

                            let mayAccessSystemRoute = false;
                            if (route.systemRoute) {
                                mayAccessSystemRoute = await this.burrowParentHierarchy(route, featureKeys);
                                // console.log('checkAuthorized > burrowParentHierarchy > mayAccessBySystemFeature:', mayAccessSystemRoute);
                            }

                            const routeModule = moduleForRoute(route);
                            // A system route (section dashboard) is open when ANY of its modules is on.
                            const moduleOn = isRouteModuleOn(route, (m) => this.moduleSwitch.isOn(m, moduleCtx));
                            if (!moduleOn) {
                                console.warn(`AuthGuard > module <${routeModule}> is dark for route <${route.route}>.`);
                                routeFeatureExists = false;
                                continue;
                            }

                            // Developers can access all routes (inside an enabled module).
                            // Public routes are accessible to any authenticated user.
                            if (
                                userAuth.user.isDeveloper ||
                                route.publicRoute ||
                                featureKeyFound ||
                                mayAccessSystemRoute
                            ) {
                                routeFeatureExists = true;

                                if (userAuth.user.isDeveloper && featureKeyFound === undefined)
                                    console.warn(
                                        `Developer user <${userAuth.user?.email}> is missing feature <${route?.routeFeature}> for route <${route.route}>.`
                                    );
                            } else {
                                routeFeatureExists = false;
                            }
                            // });
                        }

                        // setup permissions meta
                        const org = userAuth.user.organizations[userAuth.user.organizations.selected];

                        const permissions = [];
                        for (const permission of org?.permissions || []) {
                            permissions.push(permission);
                        }

                        meta['permissions'] = permissions;

                        if (userAuth.authenticated !== true) {
                            userAuth.authorized = false;
                            userAuth.redirect = userAuth.redirect || '/app/home';
                            resolve(userAuth);
                        } else if (routeFeatureExists) {
                            userAuth.authorized = true;
                            userAuth['meta'] = meta;
                            metaObject['userAuth'] = userAuth;
                            this.security.userAuth = userAuth;
                            resolve(userAuth);
                        } else {
                            userAuth.authorized = false;
                            userAuth.redirect = userAuth.redirect || '/app/home';
                            resolve(userAuth);
                        }
                    });
            })
        );
    }

    private async burrowParentHierarchy(currentRoute: NewgenusRoute, featureKeys: string[]) {
        return new Promise<boolean>((resolve) => {
            this.store
                .select(selectAllRoutes)
                .pipe(first())
                .toPromise()
                .then((allRoutes) => {
                    const mayAccessSystemRoute: boolean =
                        allRoutes
                            ?.filter((r) => r.systemRoute)
                            ?.some((sr) => {
                                return (
                                    (!sr.parentFeatures || sr.parentFeatures.length === 0) &&
                                    // Check the the current route is listed as a feature in the parent route.
                                    sr.features.includes(currentRoute.routeFeature) &&
                                    // Check that the features the user has access to includes a match in the parent route.
                                    sr.features.some((rFeatureKey) => featureKeys.includes(rFeatureKey))
                                );
                            }) || false;

                    resolve(mayAccessSystemRoute);
                });
        });
    }

    private fetchRoutesByURL(url: string): Observable<NewgenusRoute[]> {
        // strip query params
        const strippedUrl = url.split('?')[0];

        return this.store.pipe(select(areRoutesLoaded)).pipe(
            take(1),
            switchMap((routesAreLoaded) => {
                if (routesAreLoaded) {
                    return (
                        this.store
                            // Fetch all matching routes.
                            .pipe(select(selectRouteByURL(strippedUrl)))
                            .pipe(map((resp) => ({ routes: resp, from: 'ngrx' })))
                    );
                } else {
                    return this.db
                        .collection<NewgenusRoute>('routes')
                        .get()
                        .pipe(
                            map((routes) => {
                                // Parse from Firestore Documents.
                                const allRoutes = routes.docs.map((doc) => doc.data());

                                // Dispatch the fetched routes to the store.
                                this.store.dispatch(allRoutesLoaded({ routes: allRoutes }));

                                // Filter matching routes.
                                const matchingRoutes = allRoutes.filter((routeDoc) => {
                                    return routeMatchesUrl(routeDoc, strippedUrl);
                                });

                                // Return the matching routes.
                                return { routes: matchingRoutes, from: 'firestore' };
                            })
                        );
                }
            }),
            // tap((result => console.log(result))),
            map((results) => results.routes)
        );
    }
}
